import { Mail, Phone, MessageCircle, MapPin } from "lucide-react";
import { BRAND } from "../data/site";
import { useReveal } from "../hooks/useReveal";
import LeadForm from "./LeadForm";

export default function Inquiry() {
    const ref = useReveal();
    return (
        <section
            id="contact"
            data-testid="inquiry-section"
            className="bg-white py-24 sm:py-32"
        >
            <div className="mx-auto max-w-7xl px-5 sm:px-8 lg:px-10">
                <div ref={ref} className="reveal mb-14 max-w-3xl">
                    <span className="text-xs uppercase tracking-[0.22em] text-brand-red">
                        Inquiry
                    </span>
                    <h2 className="mt-3 font-display text-4xl font-bold tracking-[-0.025em] sm:text-6xl">
                        Let&apos;s talk volumes.
                    </h2>
                    <p className="mt-4 text-ink-muted">
                        Tell us what you&apos;re sourcing — we respond to export enquiries within
                        24 hours with grades, pricing and shipping options.
                    </p>
                </div>

                <div className="grid gap-8 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <LeadForm source="homepage_inquiry" />
                    </div>

                    <div className="grid gap-4 lg:col-span-5">
                        <ContactCard
                            icon={MessageCircle}
                            title="WhatsApp (Export Team)"
                            value="+91 94910 85798"
                            href={`https://wa.me/${BRAND.leadWhatsapp}`}
                            tone="turmeric"
                            testid="contact-whatsapp"
                        />
                        <ContactCard
                            icon={Mail}
                            title="Email"
                            value={BRAND.email}
                            href={`mailto:${BRAND.email}`}
                            testid="contact-email"
                        />
                        <ContactCard
                            icon={Phone}
                            title="Phone"
                            value={BRAND.phone}
                            href={`tel:+${BRAND.phoneIntl}`}
                            testid="contact-phone"
                        />
                        <div className="rounded-3xl border border-hairline bg-neutral-bg p-6">
                            <div className="flex items-start gap-3">
                                <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-red/10 text-brand-red">
                                    <MapPin className="h-5 w-5" />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-ink-muted">
                                        Head Office
                                    </div>
                                    <div className="mt-1 text-sm leading-relaxed text-ink">
                                        {BRAND.head}
                                    </div>
                                </div>
                            </div>
                            <div className="mt-5 flex items-start gap-3">
                                <span className="grid h-10 w-10 place-items-center rounded-full bg-brand-turmeric/15 text-brand-turmericDeep">
                                    <MapPin className="h-5 w-5" />
                                </span>
                                <div>
                                    <div className="text-xs uppercase tracking-[0.18em] text-ink-muted">
                                        Branch Office
                                    </div>
                                    <div className="mt-1 text-sm leading-relaxed text-ink">
                                        {BRAND.branch}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}

function ContactCard({ icon: Icon, title, value, href, tone, testid }) {
    const accent = tone === "turmeric"
        ? "bg-brand-turmeric/15 text-brand-turmericDeep"
        : "bg-brand-red/10 text-brand-red";
    return (
        <a
            href={href}
            data-testid={testid}
            target={href.startsWith("http") ? "_blank" : undefined}
            rel="noreferrer"
            className="group flex items-center gap-4 rounded-3xl border border-hairline bg-white p-6 transition-all hover:-translate-y-0.5 hover:shadow-lg"
        >
            <span className={`grid h-12 w-12 place-items-center rounded-full ${accent}`}>
                <Icon className="h-5 w-5" />
            </span>
            <div className="flex-1">
                <div className="text-xs uppercase tracking-[0.18em] text-ink-muted">{title}</div>
                <div className="font-display text-lg font-semibold text-ink">{value}</div>
            </div>
        </a>
    );
}
